import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Server, Boxes, CheckCircle2, AlertCircle, ExternalLink, ArrowUpCircle } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { toast } from '@/components/ui/Toaster';
import { useDroplets } from '@/api/droplets';
import { useFleetUpgradeRoll, useStartFleetUpgrade, type FleetRollItem } from '@/api/fleet';
import { useTenants } from '@/api/tenants';
import { REGION_LABEL } from '@/lib/tier';

export function FleetRoute() {
  const { data: droplets = [], isLoading, error } = useDroplets();
  const { data: apiTenants = [] } = useTenants();

  const totalDroplets = droplets.length;
  const linkedDroplets = droplets.filter(d => d.linked_workspace_id).length;
  const totalWorkspaces = apiTenants.reduce((a, t) => a + t.workspace_count, 0);
  const byRegion = droplets.reduce<Record<string, number>>((acc, d) => {
    acc[d.region] = (acc[d.region] || 0) + 1;
    return acc;
  }, {});
  const regions = Object.entries(byRegion).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Infrastructure"
        description="DigitalOcean droplets in the operator's account, and how they map to workspaces."
      />
      <PageBody>
        {error && (
          <Card className="mb-4 border-[color:var(--danger)]/40 bg-[color:var(--danger-soft)]/50">
            <div className="px-4 py-3 flex items-center gap-3">
              <AlertCircle size={16} className="text-[color:var(--danger)] shrink-0" />
              <div>
                <div className="text-sm font-semibold text-fg">DigitalOcean API not reachable</div>
                <div className="text-xs text-fg-muted">
                  Check <code className="font-mono">DO_TOKEN</code> in{' '}
                  <code className="font-mono">backend/.env</code>, then restart the backend.
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <Stat label="Droplets" value={isLoading ? '…' : String(totalDroplets)} icon={<Server size={14} />} />
          <Stat label="Regions" value={isLoading ? '…' : String(regions.length)} />
          <Stat
            label="Linked to workspaces"
            value={isLoading ? '…' : `${linkedDroplets} / ${totalDroplets || 0}`}
            sub={
              totalDroplets > 0 && linkedDroplets < totalDroplets
                ? `${totalDroplets - linkedDroplets} unlinked`
                : totalDroplets > 0
                  ? 'All linked'
                  : undefined
            }
          />
          <Stat label="Instances" value={String(totalWorkspaces)} icon={<Boxes size={14} />} />
        </div>

        <FleetUpgradeCard />

        {regions.length > 0 && (
          <Card className="mb-4">
            <CardHeader title="By region" />
            <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {regions.map(([slug, count]) => (
                <div
                  key={slug}
                  className="rounded-md border border-border bg-surface-2/30 px-3 py-2.5"
                >
                  <div className="text-sm font-semibold text-fg">
                    {REGION_LABEL[slug] ?? slug}
                  </div>
                  <div className="text-2xs font-mono text-fg-subtle">{slug}</div>
                  <div className="text-xl font-semibold tabnum mt-1.5">
                    {count}
                    <span className="text-xs text-fg-muted ml-1 font-normal">droplet{count === 1 ? '' : 's'}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <CardHeader
            title="All droplets"
            description="Every droplet in the connected DigitalOcean account. Click a linked one to jump to its workspace."
          />
          {isLoading ? (
            <div className="px-5 py-12 text-center text-sm text-fg-muted">Loading droplets…</div>
          ) : droplets.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Server size={20} className="mx-auto text-fg-subtle mb-2" />
              <div className="text-sm font-semibold text-fg">No droplets found</div>
              <p className="text-xs text-fg-muted max-w-md mx-auto mt-1">
                Either the connected DigitalOcean account has no droplets, or{' '}
                <code className="font-mono">DO_TOKEN</code> doesn't have read access.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <THead>
                  <tr>
                    <TH>Droplet</TH>
                    <TH>Status</TH>
                    <TH>Region</TH>
                    <TH>Size</TH>
                    <TH>Public IPv4</TH>
                    <TH>Linked workspace</TH>
                  </tr>
                </THead>
                <TBody>
                  {droplets.map(d => (
                    <TR key={d.id}>
                      <TD>
                        <div className="font-medium text-fg">{d.name}</div>
                        <div className="text-2xs text-fg-subtle font-mono">{d.id}</div>
                      </TD>
                      <TD>
                        <Badge tone={d.status === 'active' ? 'success' : 'warn'}>
                          {d.status}
                        </Badge>
                      </TD>
                      <TD className="text-fg-muted">
                        {REGION_LABEL[d.region] ?? d.region}
                        <span className="text-2xs text-fg-subtle font-mono ml-1.5">{d.region}</span>
                      </TD>
                      <TD className="font-mono text-xs text-fg-muted">{d.size}</TD>
                      <TD className="font-mono text-xs text-fg-muted">{d.ipv4 ?? '—'}</TD>
                      <TD>
                        {d.linked_workspace_id && d.linked_tenant_slug ? (
                          <Link
                            to={`/tenants/${d.linked_tenant_slug}/workspaces/${d.linked_workspace_id}`}
                            className="inline-flex items-center gap-1 text-accent hover:underline text-sm"
                          >
                            {d.linked_workspace_id}
                            <ExternalLink size={11} />
                          </Link>
                        ) : (
                          <Badge tone="neutral">Unlinked</Badge>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </Card>

        {droplets.length > 0 && linkedDroplets < totalDroplets && (
          <div className="mt-3 text-xs text-fg-muted flex items-center gap-1.5">
            <CheckCircle2 size={11} className="text-fg-subtle" />
            Unlinked droplets can be associated with a workspace from that workspace's page.
          </div>
        )}
      </PageBody>
    </>
  );
}

const ROLL_ITEM_TONE: Record<FleetRollItem['status'], 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  pending: 'neutral',
  running: 'accent',
  ok: 'success',
  failed: 'danger',
  skipped: 'neutral',
  interrupted: 'warn',
};

function FleetUpgradeCard() {
  const { data: roll } = useFleetUpgradeRoll();
  const start = useStartFleetUpgrade();
  const [reason, setReason] = useState('');

  const running = roll?.status === 'running';
  const items = roll?.items ?? [];

  const onStart = () => {
    start.mutate(reason.trim(), {
      onSuccess: r =>
        toast.success(
          `Upgrade roll #${r.roll_id} started`,
          `${r.pending} to upgrade, ${r.skipped} skipped. One instance at a time; halts on first failure.`,
        ),
      onError: (e: any) => toast.danger('Could not start upgrade roll', e?.message ?? 'Unknown error'),
    });
    setReason('');
  };

  return (
    <Card className="mb-4">
      <CardHeader
        title="Upgrade all instances"
        description="Upgrade every customer instance, one at a time. A failed upgrade halts the roll and opens an incident — the remaining instances are never touched."
      />
      <div className="px-5 py-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason (audit-logged) — e.g. v1.0.4 security patch"
            disabled={running}
            className="flex-1 h-8 rounded-md border border-border bg-surface-2/30 px-2.5 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:border-border-strong"
          />
          <Button
            size="md"
            iconLeft={<ArrowUpCircle size={14} />}
            disabled={running || !reason.trim() || start.isPending}
            onClick={onStart}
          >
            {running ? 'Roll in progress…' : 'Upgrade all'}
          </Button>
        </div>

        {roll && roll.status !== 'idle' && (
          <div>
            <div className="text-xs text-fg-muted mb-2">
              Roll #{roll.id} — <span className="font-semibold">{roll.status}</span>
              {roll.reason ? <> · {roll.reason}</> : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {items.map(i => (
                <Badge
                  key={i.id}
                  tone={ROLL_ITEM_TONE[i.status]}
                  title={i.skip_reason || (i.status === 'failed' ? i.log_tail : '')}
                >
                  {i.workspace_id}: {i.status}
                  {i.skip_reason ? ` (${i.skip_reason})` : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <Card className="px-4 py-3.5">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold flex items-center gap-1">
        {icon && <span className="text-fg-muted">{icon}</span>}
        {label}
      </div>
      <div className="text-2xl font-semibold tabnum mt-1">{value}</div>
      {sub && <div className="text-xs text-fg-muted mt-0.5">{sub}</div>}
    </Card>
  );
}
